/**
 * Environment slice build (ADR-0031): decoded `EnvironmentProperties` in, the
 * `EnvironmentSettings` the render layer applies out. Plain data, not a THREE object:
 * the renderer and scene it applies to outlive it, and the r3f layer owns them.
 */
import { scaledRgb } from '../../utils/colorParser';
import { sRGBChannelToLinear } from '../../utils/colorSpace';
import { ToneMapper } from '../../godot/rendering/environment/toneMapping.js';
import { AmbientSource, BackgroundMode, ReflectionSource } from '../../godot/scene/environment.js';
/**
 * The white the tonemapper gets, as `Environment::_update_tonemap` (`scene/resources/environment.cpp`)
 * passes `tonemap_agx_white` for AgX: its default is Blender's 16.29, the shoulder's
 * high-clip point, and `tonemap_white` would saturate every input at or above 2.0.
 * The authored value: `resolvedWhite` applies the per-curve floors (`environment_get_white`).
 */
function whiteFor(properties) {
    return properties.tonemap_mode === ToneMapper.AGX ? properties.tonemap_agx_white : properties.tonemap_white;
}
/**
 * `clearColor` is the project's `default_clear_color`, sRGB, which BG_CLEAR_COLOR draws in place of
 * `background_color` (`render_forward_clustered.cpp:1997-1998`).
 */
export function createEnvironmentSettings(properties, clearColor) {
    const backgroundColor = properties.background_mode === BackgroundMode.BG_CLEAR_COLOR ? clearColor : properties.background_color;
    return {
        background: {
            mode: properties.background_mode,
            color: scaledRgb(backgroundColor, properties.background_energy_multiplier),
            energyMultiplier: properties.background_energy_multiplier,
            canvasMaxLayer: properties.background_canvas_max_layer,
        },
        ...ambientFor(properties, backgroundColor),
        skyReflection: reflectsSky(properties),
        toneMapping: {
            mode: properties.tonemap_mode,
            exposure: properties.tonemap_exposure,
            white: whiteFor(properties),
            agxContrast: properties.tonemap_agx_contrast,
        },
        fog: properties.fog_enabled ? depthFogFor(properties) : null,
        skyRotation: properties.sky_rotation,
        volumetricFog: properties.volumetric_fog_enabled ? volumetricFogFor(properties) : null,
        ssr: properties.ssr_enabled ? ssrFor(properties) : null,
        adjustments: properties.adjustment_enabled ? adjustmentsFor(properties) : null,
        glow: properties.glow_enabled
            ? {
                levels: glowLevelsFor(properties),
                intensity: properties.glow_intensity,
                strength: properties.glow_strength,
                mix: properties.glow_mix,
                bloom: properties.glow_bloom,
                blendMode: properties.glow_blend_mode,
                hdrThreshold: properties.glow_hdr_threshold,
                hdrScale: properties.glow_hdr_scale,
                luminanceCap: properties.glow_hdr_luminance_cap,
                map: properties.glow_map,
                mapStrength: properties.glow_map_strength,
            }
            : null,
    };
}
/**
 * `environment_set_adjustment` stores the brightness converted from sRGB to linear
 * (`environment_storage.cpp:906`), and the shader multiplies linear values by it.
 */
function adjustmentsFor(properties) {
    return {
        brightness: sRGBChannelToLinear(properties.adjustment_brightness),
        contrast: properties.adjustment_contrast,
        saturation: properties.adjustment_saturation,
    };
}
function ssrFor(properties) {
    return {
        maxSteps: properties.ssr_max_steps,
        fadeIn: properties.ssr_fade_in,
        fadeOut: properties.ssr_fade_out,
        depthTolerance: properties.ssr_depth_tolerance,
    };
}
function depthFogFor(properties) {
    return {
        mode: properties.fog_mode,
        color: properties.fog_light_color,
        lightEnergy: properties.fog_light_energy,
        sunScatter: properties.fog_sun_scatter,
        density: properties.fog_density,
        aerialPerspective: properties.fog_aerial_perspective,
        skyAffect: properties.fog_sky_affect,
        height: properties.fog_height,
        heightDensity: properties.fog_height_density,
        depthCurve: properties.fog_depth_curve,
        depthBegin: properties.fog_depth_begin,
        depthEnd: properties.fog_depth_end,
    };
}
function volumetricFogFor(properties) {
    return {
        density: properties.volumetric_fog_density,
        albedo: properties.volumetric_fog_albedo,
        emission: properties.volumetric_fog_emission,
        emissionEnergy: properties.volumetric_fog_emission_energy,
        anisotropy: properties.volumetric_fog_anisotropy,
        length: properties.volumetric_fog_length,
        detailSpread: properties.volumetric_fog_detail_spread,
        ambient: {
            inject: properties.volumetric_fog_ambient_inject * properties.ambient_light_energy,
            color: properties.ambient_light_color,
            skyContribution: properties.ambient_light_sky_contribution,
        },
        skyAffect: properties.volumetric_fog_sky_affect,
        temporalReprojection: properties.volumetric_fog_temporal_reprojection_enabled
            ? properties.volumetric_fog_temporal_reprojection_amount
            : null,
    };
}
/**
 * `Environment::_update_glow`'s level weights: sum-normalised under
 * `glow_normalized`, passed through otherwise. Godot divides by the sum with no
 * guard, so an all-zero set would hand the shader NaN. Weights that sum to zero
 * contribute nothing either way, so they pass through instead.
 */
function glowLevelsFor(properties) {
    const levels = properties.glow_levels;
    if (!properties.glow_normalized)
        return levels;
    const sum = levels.reduce((total, weight) => total + weight, 0);
    return sum > 0 ? levels.map((weight) => weight / sum) : levels;
}
/**
 * Whether a surface reflects the sky, as `RenderSceneDataRD::update_ubo` sets the reflection
 * cubemap flag (`render_scene_data_rd.cpp:196-200`): the background source over a sky background,
 * or the sky source whatever the background. The ambient source plays no part.
 */
function reflectsSky(properties) {
    const source = properties.reflected_light_source;
    return ((source === ReflectionSource.BG && properties.background_mode === BackgroundMode.BG_SKY) ||
        source === ReflectionSource.SKY);
}
/**
 * The ambient this Environment contributes, transcribed from
 * `RenderSceneDataRD::update_ubo`, which fills the shader's ambient
 * (`sky_bake_panorama` is the baking path, a different table). Colours stay in
 * Godot space: the consumer converts sRGB to linear.
 */
function ambientFor(properties, backgroundColor) {
    const source = properties.ambient_light_source;
    const background = properties.background_mode;
    // BG (0, the default source) over BG_CLEAR_COLOR or BG_COLOR: flat = that colour
    // × background_energy_multiplier.
    if (source === AmbientSource.BG &&
        (background === BackgroundMode.BG_CLEAR_COLOR || background === BackgroundMode.BG_COLOR)) {
        return {
            ambient: {
                color: backgroundColor,
                energy: properties.background_energy_multiplier,
            },
            skyAmbient: null,
        };
    }
    // Otherwise flat = ambient_light_color × ambient_light_energy, and the cubemap is
    // used for (BG + BG_SKY) or SKY.
    const overSky = background === BackgroundMode.BG_SKY;
    const fromCubemap = (source === AmbientSource.BG && overSky) || source === AmbientSource.SKY;
    // The shader blends ambient = mix(flat, sky × background_energy_multiplier,
    // sky_contribution), folded into the energies: at the default 1.0 the flat term is
    // zero, so `ambient_light_color` under AmbientSource.SKY leaves no trace in Godot.
    // A COLOR or DISABLED source takes none from the sky, which is still reflected.
    const contribution = fromCubemap ? properties.ambient_light_sky_contribution : 0;
    // The sky mounts for the diffuse share only when it is the background or the ambient source:
    // `contribution` scales the ambient apart from the reflection, which `reflectsSky` decides.
    const skyAmbient = fromCubemap || overSky ? { contribution } : null;
    if (!fromCubemap && source !== AmbientSource.COLOR) {
        return { ambient: null, skyAmbient };
    }
    return {
        ambient: {
            color: properties.ambient_light_color,
            energy: properties.ambient_light_energy * (1 - contribution),
        },
        skyAmbient,
    };
}
//# sourceMappingURL=build.js.map